/**
 * The "Make more space" offer on a device health alert.
 *
 * A disk or memory alert from the desktop app can open the space screen
 * (DeviceSpace), which measures what can be cleared and asks before it
 * clears anything. Only the computer the alert is about can do that, so the
 * offer is shown only when the alert names this computer's own device id.
 */

export type DeviceSpaceFocus = "disk" | "memory";

export type DeviceSpaceOffer = {
  focus: DeviceSpaceFocus;
  level: "warning" | "critical";
  /** Bytes the device reported it can clear for `focus`, when it measured them. */
  reclaimableBytes: number | null;
  /** The id the desktop app reported for itself. Untrusted; used only to match this computer. */
  reportedDeviceId: string | null;
};

export const DEVICE_SPACE_ACTION_LABEL = "Make more space";

/** The space screen opens by itself for a critical alert, or when this much can be cleared. */
export const DEVICE_SPACE_AUTO_OPEN_BYTES = 1e9;

type Issue = { metric: DeviceSpaceFocus; level: "warning" | "critical" };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isFocus = (value: unknown): value is DeviceSpaceFocus => value === "disk" || value === "memory";

const isLevel = (value: unknown): value is Issue["level"] => value === "warning" || value === "critical";

/** Feed rows carry stored extras at the top level; rows built for display keep them under `extraFields`. */
function deviceHealthFacts(item: Record<string, unknown>): Record<string, unknown> | null {
  if (isRecord(item.deviceHealth)) return item.deviceHealth;
  const extraFields = item.extraFields;
  if (isRecord(extraFields) && isRecord(extraFields.deviceHealth)) return extraFields.deviceHealth;
  return null;
}

function issuesOf(categories: string[], facts: Record<string, unknown> | null): Issue[] {
  if (facts && Array.isArray(facts.issues)) {
    return facts.issues.flatMap((entry): Issue[] =>
      isRecord(entry) && isFocus(entry.metric) && isLevel(entry.level) ? [{ metric: entry.metric, level: entry.level }] : [],
    );
  }
  // Rows without stored issues still name their level and metrics in categories.
  const level = categories.includes("critical") ? "critical" : categories.includes("warning") ? "warning" : null;
  if (!level) return [];
  return categories.filter(isFocus).map((metric) => ({ metric, level }));
}

function bytesFor(facts: Record<string, unknown> | null, metric: DeviceSpaceFocus): number | null {
  const reclaimable = facts && isRecord(facts.reclaimableBytes) ? facts.reclaimableBytes[metric] : undefined;
  return typeof reclaimable === "number" && Number.isFinite(reclaimable) && reclaimable > 0 ? reclaimable : null;
}

/** The worse problem; on a tie, the one with more to clear; then disk. */
function mostPressing(issues: Issue[], facts: Record<string, unknown> | null): Issue {
  return [...issues].sort((left, right) => {
    const severity = Number(right.level === "critical") - Number(left.level === "critical");
    if (severity !== 0) return severity;
    const bytes = (bytesFor(facts, right.metric) ?? 0) - (bytesFor(facts, left.metric) ?? 0);
    if (bytes !== 0) return bytes;
    return Number(right.metric === "disk") - Number(left.metric === "disk");
  })[0];
}

/**
 * The space offer a device health row makes, or null. Battery, network,
 * recovered and healthy rows make none: there is nothing to clear.
 */
export function readDeviceSpaceOffer(item: unknown): DeviceSpaceOffer | null {
  if (!isRecord(item)) return null;
  if (String(item.source ?? "").toLowerCase() !== "device_health") return null;
  const categories = Array.isArray(item.categories) ? item.categories.map((entry) => String(entry)) : [];
  if (categories.some((entry) => entry === "recovered" || entry === "healthy" || entry === "battery_runway")) return null;
  const facts = deviceHealthFacts(item);
  const issues = issuesOf(categories, facts);
  if (issues.length === 0) return null;

  const lead = mostPressing(issues, facts);
  const reportedDeviceId = facts && typeof facts.reportedDeviceId === "string" && facts.reportedDeviceId
    ? facts.reportedDeviceId
    : null;
  return { focus: lead.metric, level: lead.level, reclaimableBytes: bytesFor(facts, lead.metric), reportedDeviceId };
}

/** True when the offer is about the computer this app runs on. Web and other computers get no offer. */
export function isOfferForThisDevice(offer: DeviceSpaceOffer | null, thisDeviceId: string | null | undefined): offer is DeviceSpaceOffer {
  return Boolean(offer && thisDeviceId && offer.reportedDeviceId === thisDeviceId);
}

/** Whether the space screen should open without being asked. */
export function shouldAutoOpenDeviceSpace(offer: DeviceSpaceOffer): boolean {
  return offer.level === "critical" || (offer.reclaimableBytes ?? 0) >= DEVICE_SPACE_AUTO_OPEN_BYTES;
}

/**
 * One automatic open per alert. An episode keeps its id and createdDate for
 * as long as the problem lasts, so this key opens the screen once per episode
 * and once more each time it turns critical again after easing.
 */
export function deviceSpaceAutoOpenKey(item: unknown): string | null {
  if (!isRecord(item)) return null;
  const id = typeof item.id === "string" && item.id ? item.id : typeof item._key === "string" && item._key ? item._key : null;
  const created = typeof item.createdDate === "number" && Number.isFinite(item.createdDate) ? item.createdDate : null;
  if (!id || created === null) return null;
  // A critical problem that eased and turned critical again (the disk filled
  // up again after being freed) counts as a new alert, so the screen opens
  // once more for it. Short dips do not count (deviceHealthEpisodes).
  const peak = deviceHealthFacts(item)?.peak;
  const escalations = isRecord(peak) && typeof peak.escalations === "number" && Number.isInteger(peak.escalations) && peak.escalations > 0
    ? peak.escalations
    : 0;
  return escalations > 0 ? `${id}:${created}:e${escalations}` : `${id}:${created}`;
}

/**
 * The problem that may open the space screen by itself now, and at which
 * level. The server says per problem how serious it has stayed long enough
 * to open the screen (`autoOpenLevels`): one passing memory spike opens
 * nothing. Rows written before it said so go by the offer.
 */
function autoOpening(item: Record<string, unknown>, offer: DeviceSpaceOffer): Issue | null {
  const facts = deviceHealthFacts(item);
  const levels = facts?.autoOpenLevels;
  if (!isRecord(levels)) return shouldAutoOpenDeviceSpace(offer) ? { metric: offer.focus, level: offer.level } : null;
  const ready = (["disk", "memory"] as const).flatMap((metric): Issue[] => {
    const level = levels[metric];
    if (!isLevel(level)) return [];
    return level === "critical" || (bytesFor(facts, metric) ?? 0) >= DEVICE_SPACE_AUTO_OPEN_BYTES ? [{ metric, level }] : [];
  });
  return ready.length > 0 ? mostPressing(ready, facts) : null;
}

/**
 * The alert that should open the space screen by itself now, or null. Feed
 * entries arrive most important first, so the first one that qualifies wins.
 * It must be about this computer, serious enough, and not opened before at
 * its level or a higher one: an alert that opened the screen as a warning
 * opens it once more when it turns critical, but one that opened it as
 * critical stays quiet when it eases to a warning.
 */
export function deviceSpaceToAutoOpen(
  entries: readonly unknown[],
  thisDeviceId: string | null | undefined,
  alreadyOpened: ReadonlySet<string>,
): { key: string; focus: DeviceSpaceFocus } | null {
  for (const entry of entries) {
    const offer = readDeviceSpaceOffer(entry);
    if (!isOfferForThisDevice(offer, thisDeviceId) || !isRecord(entry)) continue;
    const opening = autoOpening(entry, offer);
    const episodeKey = deviceSpaceAutoOpenKey(entry);
    if (!opening || episodeKey === null) continue;
    const key = `${episodeKey}:${opening.level}`;
    if (alreadyOpened.has(`${episodeKey}:critical`) || alreadyOpened.has(key)) continue;
    return { key, focus: opening.metric };
  }
  return null;
}
